import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useId,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  // Card,
  // CardContent,
  Button,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  // IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Search from "@mui/icons-material/Search";
// import EmailOutlined from "@mui/icons-material/EmailOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import AdminPharmacyHaloStatCard from "@/components/admin/AdminPharmacyHaloStatCard";
import AdminBoolCell from "@/components/admin/AdminBoolCell";
import AdminQudaStatCard from "@/components/admin/AdminQudaStatCard";
import AdminMediDropStatCard from "@/components/admin/AdminMediDropStatCard";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { GroupOutlined, CreditCardOutlined } from "@mui/icons-material";
import {
  normalizeAdminOverviewResponse,
  mapAdminOverviewTableRow,
  AUSTRALIAN_STATE_MENU,
  normalizeAustralianStateCode,
} from "@/utils/adminDashboardUtils";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { getChemistName } from "@/utils/textUtils";
import { PhoneLink } from "@/utils/PhoneLink";
import { dispenseSystemName } from "@/config/dispenseSystems";

const adminOverviewDataGridSx = {
  ...pharmacyHaloDataGridSx,
  "& .MuiDataGrid-columnHeaders": {
    ...(pharmacyHaloDataGridSx["& .MuiDataGrid-columnHeaders"] || {}),
    minHeight: "auto",
  },
  "& .MuiDataGrid-columnHeader": {
    ...(pharmacyHaloDataGridSx["& .MuiDataGrid-columnHeader"] || {}),
    minHeight: 28,
    maxHeight: "none",
    py: 0.25,
    px: 0.5,
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    padding: 0,
    gap: 0.25,
  },
  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
    overflow: "visible",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    ...(pharmacyHaloDataGridSx["& .MuiDataGrid-columnHeaderTitle"] || {}),
    fontSize: "0.625rem",
    fontWeight: 600,
    letterSpacing: "0.01em",
    lineHeight: 1.15,
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
  },
  "& .MuiDataGrid-sortIcon": {
    fontSize: "0.9rem",
  },
  "& .MuiDataGrid-cell": {
    ...(pharmacyHaloDataGridSx["& .MuiDataGrid-cell"] || {}),
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    // Remove default cell inset so conditional fills read as full tiles (no white gutters).
    p: "0 !important",
    "--DataGrid-cell-padding": "0px",
  },
  "& .MuiDataGrid-cell.admin-overview-cell--alignLeft": {
    justifyContent: "flex-start",
    pl: 1,
    pr: 0.5,
  },
};

function qudaStatusLabel(raw) {
  if (raw === true || raw === "true" || raw === 1) return "Yes";
  if (raw === false || raw === "false" || raw === 0) return "No";
  const s = String(raw ?? "").trim();
  return s.length ? s : "—";
}

function mediDropStatusLabel(raw) {
  if (raw == null || raw === "") return "—";
  const s = String(raw).trim();
  if (!s || s.toLowerCase() === "none") return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function matchesTriStateBool(value, filter) {
  if (filter === "all") return true;
  const on = value === true || value === "true" || value === 1;
  if (filter === "yes") return on;
  if (filter === "no") return !on;
  return true;
}

/** Pastel fill + text for Quda orders (30d total): 0 red, 1–90 pink, 91–210 yellow, 211–300 light yellow, >300 green. */
function qudaOrders30dCellStyle(n) {
  if (!Number.isFinite(n)) {
    return { backgroundColor: "#f3f4f6", color: "#4b5563" };
  }
  const v = Math.trunc(n);
  if (v === 0) {
    return { backgroundColor: "#fecaca", color: "#b91c1c" };
  }
  if (v > 300) {
    return { backgroundColor: "#bbf7d0", color: "#166534" };
  }
  if (v <= 90) {
    return { backgroundColor: "#fbcfe8", color: "#9d174d" };
  }
  if (v <= 210) {
    return { backgroundColor: "#fef08a", color: "#a16207" };
  }
  return { backgroundColor: "#fef9c3", color: "#854d0e" };
}

/** SMS (30d total): ≤150 red, 151–300 yellow, >300 green. */
function sms30dCellStyle(n) {
  if (!Number.isFinite(n)) {
    return { backgroundColor: "#f3f4f6", color: "#4b5563" };
  }
  const v = Math.trunc(n);
  if (v <= 150) return { backgroundColor: "#fecaca", color: "#b91c1c" };
  if (v <= 300) return { backgroundColor: "#fef08a", color: "#a16207" };
  return { backgroundColor: "#bbf7d0", color: "#166534" };
}

/** Payments (30d total): 0 red, 1–90 yellow, >90 green. */
function payments30dCellStyle(n) {
  if (!Number.isFinite(n)) {
    return { backgroundColor: "#f3f4f6", color: "#4b5563" };
  }
  const v = Math.trunc(n);
  if (v === 0) return { backgroundColor: "#fecaca", color: "#b91c1c" };
  if (v <= 90) return { backgroundColor: "#fef08a", color: "#a16207" };
  return { backgroundColor: "#bbf7d0", color: "#166534" };
}

/** Consults (30d total): 0 red, 1–60 yellow, >60 green. */
function consults30dCellStyle(n) {
  if (!Number.isFinite(n)) {
    return { backgroundColor: "#f3f4f6", color: "#4b5563" };
  }
  const v = Math.trunc(n);
  if (v === 0) return { backgroundColor: "#fecaca", color: "#b91c1c" };
  if (v <= 60) return { backgroundColor: "#fef08a", color: "#a16207" };
  return { backgroundColor: "#bbf7d0", color: "#166534" };
}

/** PCs & Staff: 0–1 red, 2–3 yellow, ≥4 green. */
function pcOrStaffCountCellStyle(n) {
  if (!Number.isFinite(n)) {
    return { backgroundColor: "#f3f4f6", color: "#4b5563" };
  }
  const v = Math.trunc(n);
  if (v <= 1) return { backgroundColor: "#fecaca", color: "#b91c1c" };
  if (v <= 3) return { backgroundColor: "#fef08a", color: "#a16207" };
  return { backgroundColor: "#bbf7d0", color: "#166534" };
}

function AdminOverviewMetricCell({ n, styleFn }) {
  const { backgroundColor, color } = styleFn(n);
  const display = Number.isFinite(n) ? String(Math.trunc(n)) : "—";
  return (
    <Box
      sx={{
        flex: 1,
        width: 1,
        minHeight: 36,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "stretch",
        backgroundColor,
        color,
        px: 0.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {display}
      </Typography>
    </Box>
  );
}

/** Drop duplicate ChemistUUID rows (API can duplicate when detail joins were used). */
function dedupeChemistTableRows(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const row of raw) {
    const uuid = String(row?.ChemistUUID ?? "").trim();
    if (uuid) {
      if (seen.has(uuid)) continue;
      seen.add(uuid);
    }
    out.push(row);
  }
  return out;
}

const AdminOverviewPage = ({ isManager = false }) => {
  const navigate = useNavigate();
  const chemistBasePath = isManager ? "/manager-functions/chemist" : "/admin-functions/chemist";
  const dispenseTypeFilterLabelId = useId();
  const qudaStatusFilterLabelId = useId();
  const mediDropFilterLabelId = useId();
  const haloPayFilterLabelId = useId();
  const stateFilterLabelId = useId();
  const groupFilterLabelId = useId();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [normalized, setNormalized] = useState(null);
  const [filterChemistName, setFilterChemistName] = useState("");
  const [filterDispenseType, setFilterDispenseType] = useState("all");
  const [filterQudaStatus, setFilterQudaStatus] = useState("all");
  const [filterMediDropStatus, setFilterMediDropStatus] = useState("all");
  const [filterHaloPayStatus, setFilterHaloPayStatus] = useState("all");
  const [filterChemistState, setFilterChemistState] = useState("all");
  const [filterGroupID, setFilterGroupID] = useState("all");

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(
        isManager ? API_PATHS.MANAGER_FUNCTIONS_OVERVIEW : API_PATHS.ADMIN_FUNCTIONS_OVERVIEW,
        "GET",
        null,
        {}
      );
      setNormalized(normalizeAdminOverviewResponse(res));
    } catch (e) {
      console.error(e);
      setError(getAdminApiErrorMessage(e, "Failed to load admin overview"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const rows = useMemo(() => {
    const list = dedupeChemistTableRows(normalized?.chemistTable);
    return list.map((row, i) => mapAdminOverviewTableRow(row, i));
  }, [normalized]);

  const dispenseTypeFilterOptions = useMemo(() => {
    const values = new Set();
    let hasEmpty = false;
    for (const r of rows) {
      const v = r.dispenseType;
      if (v == null || v === "") hasEmpty = true;
      else values.add(String(v));
    }
    const sorted = [...values].sort((a, b) => Number(a) - Number(b));
    return { sorted, hasEmpty };
  }, [rows]);

  const qudaStatusFilterOptions = useMemo(() => {
    const labels = new Set();
    for (const r of rows) labels.add(qudaStatusLabel(r.qudaActive));
    return [...labels].sort((a, b) => {
      if (a === "—") return 1;
      if (b === "—") return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
  }, [rows]);

  const mediDropStatusFilterOptions = useMemo(() => {
    const labels = new Set();
    for (const r of rows) labels.add(mediDropStatusLabel(r.mediDropActive));
    return [...labels].sort((a, b) => {
      if (a === "—") return 1;
      if (b === "—") return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = filterChemistName.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const name = String(getChemistName(r.chemistName) || "").toLowerCase();
        if (!name.includes(q)) return false;
      }
      if (filterDispenseType !== "all") {
        if (filterDispenseType === "__empty__") {
          if (r.dispenseType != null && String(r.dispenseType).trim() !== "") {
            return false;
          }
        } else if (String(r.dispenseType ?? "") !== filterDispenseType) {
          return false;
        }
      }
      if (filterQudaStatus !== "all") {
        if (qudaStatusLabel(r.qudaActive) !== filterQudaStatus) return false;
      }
      if (filterMediDropStatus !== "all") {
        if (mediDropStatusLabel(r.mediDropActive) !== filterMediDropStatus) return false;
      }
      if (!matchesTriStateBool(r.haloPayActive, filterHaloPayStatus)) {
        return false;
      }
      if (filterChemistState !== "all") {
        if (
          normalizeAustralianStateCode(r.chemistState) !== filterChemistState
        ) {
          return false;
        }
      }
      if (filterGroupID !== "all") {
        if (String(r.groupID ?? "") !== filterGroupID) return false;
      }
      return true;
    });
  }, [
    rows,
    filterChemistName,
    filterDispenseType,
    filterQudaStatus,
    filterMediDropStatus,
    filterHaloPayStatus,
    filterChemistState,
    filterGroupID,
  ]);

  const columns = [
    {
      field: "chemistName",
      headerName: "Chemist Name",
      flex: 1,
      minWidth: 100,
      cellClassName: "admin-overview-cell--alignLeft",
      renderCell: (p) => (
        <Typography
          variant="body2"
          noWrap
          title={getChemistName(p.value) || undefined}
          onClick={(e) => {
            e.stopPropagation();
            p.row.chemistUUID && navigate(`${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`);
          }}
          sx={{
            width: 1, minWidth: 0, fontSize: "0.95rem", fontWeight: 500,
            cursor: p.row.chemistUUID ? "pointer" : "default",
            "&:hover": { textDecoration: p.row.chemistUUID ? "underline" : "none" },
          }}
        >
          {getChemistName(p.value)}
        </Typography>
      ),
    },
    {
      field: "chemistID",
      headerName: "ID",
      width: 72,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => {
        const n = Number(row?.chemistID);
        return Number.isFinite(n) ? n : null;
      },
    },
    {
      field: "dispenseSystemName",
      headerName: "Dispense",
      flex: 0.42,
      minWidth: 132,
      cellClassName: "admin-overview-cell--alignLeft",
      renderCell: (p) => {
        const t = String(p.value ?? "").trim();
        return (
          <Box
            sx={{
              flex: 1,
              width: 1,
              minHeight: 0,
              alignSelf: "stretch",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              noWrap
              title={t || undefined}
              sx={{ width: 1, minWidth: 0 }}
            >
              {t || "—"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "broadcastActive",
      headerName: "Broadcast",
      width: 72,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Box sx={{ flex: 1, alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <AdminBoolCell value={p.value} />
        </Box>
      ),
    },
    {
      field: "qudaActive",
      headerName: "Quda",
      width: 78,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => {
        const display = qudaStatusLabel(p.value);
        const isActiveWord =
          String(p.value ?? "").trim().toLowerCase() === "active";
        return (
          <Box
            sx={{
              flex: 1,
              width: 1,
              minHeight: 0,
              alignSelf: "stretch",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              textAlign="center"
              sx={{
                width: 1,
                minWidth: 0,
                fontWeight: isActiveWord ? 600 : 400,
                color: isActiveWord ? "success.main" : "text.primary",
              }}
            >
              {display}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "mediDropActive",
      headerName: "MediDrop",
      width: 78,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => {
        const raw = p.value;
        const display = mediDropStatusLabel(raw);
        const lower = String(raw ?? "").trim().toLowerCase();
        const isActive = lower === "active";
        const isCancelled = lower === "cancelled";
        return (
          <Box
            sx={{
              flex: 1,
              width: 1,
              minHeight: 0,
              alignSelf: "stretch",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              textAlign="center"
              sx={{
                width: 1,
                minWidth: 0,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "success.main" : isCancelled ? "#dc2626" : "text.primary",
              }}
            >
              {display}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "haloPayActive",
      headerName: "HaloPay",
      width: 76,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Box sx={{ flex: 1, alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <AdminBoolCell value={p.value} />
        </Box>
      ),
    },
    {
      field: "docLinkActive",
      headerName: "DocLink",
      width: 76,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Box sx={{ flex: 1, alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <AdminBoolCell value={p.value} />
        </Box>
      ),
    },
    {
      field: "totalTerminals",
      headerName: "PCs",
      width: 52,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => {
        const n = Number(row?.totalTerminals);
        return Number.isFinite(n) ? n : null;
      },
      renderCell: (p) => (
        <AdminOverviewMetricCell
          n={Number(p.row?.totalTerminals)}
          styleFn={pcOrStaffCountCellStyle}
        />
      ),
    },
    {
      field: "totalStaff",
      headerName: "Staff",
      width: 56,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => {
        const n = Number(row?.totalStaff);
        return Number.isFinite(n) ? n : null;
      },
      renderCell: (p) => (
        <AdminOverviewMetricCell
          n={Number(p.row?.totalStaff)}
          styleFn={pcOrStaffCountCellStyle}
        />
      ),
    },
    {
      field: "totalQudaOrders",
      headerName: "Orders",
      width: 64,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => {
        const n = Number(row?.totalQudaOrders);
        return Number.isFinite(n) ? n : null;
      },
    },
    {
      field: "qudaOrders30Days",
      headerName: "Orders (30d)",
      width: 96,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => {
        const n = Number(row?.qudaOrders30Days);
        return Number.isFinite(n) ? n : null;
      },
      renderCell: (p) => (
        <AdminOverviewMetricCell
          n={Number(p.row?.qudaOrders30Days)}
          styleFn={qudaOrders30dCellStyle}
        />
      ),
    },
    {
      field: "sms30Days",
      headerName: "SMS (30d)",
      width: 82,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => {
        const n = Number(row?.sms30Days);
        return Number.isFinite(n) ? n : null;
      },
      renderCell: (p) => (
        <AdminOverviewMetricCell
          n={Number(p.row?.sms30Days)}
          styleFn={sms30dCellStyle}
        />
      ),
    },
    {
      field: "consults30Days",
      headerName: "Consults (30d)",
      width: 96,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => {
        const n = Number(row?.consults30Days);
        return Number.isFinite(n) ? n : null;
      },
      renderCell: (p) => (
        <AdminOverviewMetricCell
          n={Number(p.row?.consults30Days)}
          styleFn={consults30dCellStyle}
        />
      ),
    },
    {
      field: "haloPayTransactions30Days",
      headerName: "Payments (30d)",
      width: 102,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => {
        const n = Number(row?.haloPayTransactions30Days);
        return Number.isFinite(n) ? n : null;
      },
      renderCell: (p) => (
        <AdminOverviewMetricCell
          n={Number(p.row?.haloPayTransactions30Days)}
          styleFn={payments30dCellStyle}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Open",
      width: 86,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`
            );
          }}
          disabled={!p.row.chemistUUID}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: PHARMACY_HALO_COLORS.BLUE,
            minWidth: 60,
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  const sc = normalized?.summaryCards || {};

  if (loading && !normalized) {
    return (
      <Box className="flex items-center justify-center min-h-[320px]">
        <Typography color="textSecondary">Loading admin overview…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex items-center justify-center min-h-[320px] px-4">
        <Alert severity="warning" sx={{ maxWidth: 560 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <div className="space-y-4 min-h-screen bg-white p-4 min-w-0 max-w-full">
      <Typography
        variant="h5"
        className="font-bold"
        sx={{ color: PHARMACY_HALO_COLORS.BLUE }}
      >
        Admin overview
      </Typography>

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <AdminPharmacyHaloStatCard
          title="Total Chemists"
          value={sc.totalChemistsUsingApps ?? 0}
          valueFormatter={(v) => Number(v || 0).toLocaleString()}
          Icon={GroupOutlined}
        />
        <div className="min-w-0">
          <AdminQudaStatCard
            title="Quda Subscriptions"
            value={sc.qudaSubscriptions ?? 0}
            valueFormatter={(v) => Number(v || 0).toLocaleString()}
            Icon={StorefrontOutlinedIcon}
          />
        </div>
        <div className="min-w-0">
          <AdminMediDropStatCard
            title="MediDrop Subscriptions"
            value={sc.mediDropSubscriptions ?? 0}
            valueFormatter={(v) => Number(v || 0).toLocaleString()}
            Icon={LocalShippingOutlinedIcon}
          />
        </div>
        <AdminPharmacyHaloStatCard
          title="Active HaloPay"
          value={sc.activeHaloPay ?? 0}
          valueFormatter={(v) => Number(v || 0).toLocaleString()}
          Icon={CreditCardOutlined}
        />
      </section>

      {/* Selected chemist detail — moved to another page; restore when wiring a new route.
      {selectedUuid && (detail || selectedRowData) ? (
        <Card
          variant="outlined"
          sx={{
            maxWidth: 520,
            borderColor: PHARMACY_HALO_COLORS.BLUE,
            background: `linear-gradient(180deg, #fff 0%, ${PHARMACY_HALO_COLORS.LIGHT_GRAY} 100%)`,
            boxShadow: 2,
          }}
        >
          <CardContent sx={{ py: 2.5, "&:last-child": { pb: 2.5 } }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: PHARMACY_HALO_COLORS.BLUE, mb: 1 }}
            >
              {getChemistName(detail?.ChemistName ?? selectedRowData?.chemistName)}
            </Typography>
            {detail ? (
              <>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Phone:</strong> <PhoneLink phone={detail.Phone} />
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" component="span">
                    <strong>Email:</strong> {detail.Email ?? "—"}
                  </Typography>
                  {detail.Email ? (
                    <IconButton
                      size="small"
                      color="primary"
                      href={`mailto:${encodeURIComponent(detail.Email)}`}
                      aria-label="Email chemist"
                      sx={{
                        border: `1px solid ${PHARMACY_HALO_COLORS.BLUE}`,
                        borderRadius: 1,
                      }}
                    >
                      <EmailOutlined fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
                {dedicatedFromDetail != null &&
                String(dedicatedFromDetail).trim() !== "" ? (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>SMS dedicated number:</strong>{" "}
                    {String(dedicatedFromDetail)}
                  </Typography>
                ) : null}
                <Typography variant="body2" color="textSecondary">
                  Initial staff: {detail.InitialStaff ?? "—"}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                No directory detail returned for this chemist. You can still open
                the admin chemist page.
              </Typography>
            )}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() =>
                  navigate(
                    `${chemistBasePath}/${encodeURIComponent(
                      detail?.ChemistUUID ?? selectedRowData?.chemistUUID ?? ""
                    )}`
                  )
                }
                disabled={!(detail?.ChemistUUID ?? selectedRowData?.chemistUUID)}
                sx={{
                  textTransform: "none",
                  backgroundColor: PHARMACY_HALO_COLORS.BLUE,
                }}
              >
                Open chemist
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : null}
      */}

      <Box className="space-y-2">
        <Typography variant="subtitle1" className="font-semibold text-gray-800">
          All chemists
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-end",
          }}
        >
          <TextField
            size="small"
            label="Chemist name"
            placeholder="Search"
            value={filterChemistName}
            onChange={(e) => setFilterChemistName(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 150, flex: 1 }}
          />
          {normalized?.groups?.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
              <InputLabel id={groupFilterLabelId}>Group</InputLabel>
              <Select
                labelId={groupFilterLabelId}
                label="Group"
                value={filterGroupID}
                onChange={(e) => setFilterGroupID(e.target.value)}
              >
                <MenuItem value="all">All groups</MenuItem>
                {normalized.groups.map((g) => (
                  <MenuItem key={g.GroupID} value={String(g.GroupID)}>
                    {g.GroupName || `Group ${g.GroupID}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
            <InputLabel id={stateFilterLabelId}>State</InputLabel>
            <Select
              labelId={stateFilterLabelId}
              label="State"
              value={filterChemistState}
              onChange={(e) => setFilterChemistState(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {AUSTRALIAN_STATE_MENU.map(({ code, label }) => (
                <MenuItem key={code} value={code}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
            <InputLabel id={dispenseTypeFilterLabelId}>Dispense type</InputLabel>
            <Select
              labelId={dispenseTypeFilterLabelId}
              label="Dispense type"
              value={filterDispenseType}
              onChange={(e) => setFilterDispenseType(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {dispenseTypeFilterOptions.hasEmpty ? (
                <MenuItem value="__empty__">Unspecified</MenuItem>
              ) : null}
              {dispenseTypeFilterOptions.sorted.map((v) => (
                <MenuItem key={v} value={v}>
                  {dispenseSystemName(v) || v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
            <InputLabel id={qudaStatusFilterLabelId}>Quda status</InputLabel>
            <Select
              labelId={qudaStatusFilterLabelId}
              label="Quda status"
              value={filterQudaStatus}
              onChange={(e) => setFilterQudaStatus(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {qudaStatusFilterOptions.map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
            <InputLabel id={mediDropFilterLabelId}>MediDrop status</InputLabel>
            <Select
              labelId={mediDropFilterLabelId}
              label="MediDrop status"
              value={filterMediDropStatus}
              onChange={(e) => setFilterMediDropStatus(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {mediDropStatusFilterOptions.map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
            <InputLabel id={haloPayFilterLabelId}>HaloPay status</InputLabel>
            <Select
              labelId={haloPayFilterLabelId}
              label="HaloPay status"
              value={filterHaloPayStatus}
              onChange={(e) => setFilterHaloPayStatus(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setFilterChemistName("");
              setFilterDispenseType("all");
              setFilterQudaStatus("all");
              setFilterMediDropStatus("all");
              setFilterHaloPayStatus("all");
              setFilterChemistState("all");
              setFilterGroupID("all");
            }}
            sx={{ textTransform: "none", fontWeight: 600, color: "#dc2626", borderColor: "#f87171", "&:hover": { borderColor: "#dc2626", backgroundColor: "#fff1f2" } }}
          >
            Clear filters
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          height: 520,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          density="compact"
          disableColumnMenu
          pageSizeOptions={[10, 20, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 20 } },
          }}
          disableRowSelectionOnClick
          sx={{
            ...adminOverviewDataGridSx,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        />
      </Box>
      <Typography variant="caption" color="textSecondary">
        {filteredRows.length !== rows.length
          ? `Showing ${filteredRows.length} of ${rows.length} pharmacies`
          : `${rows.length} pharmacies`}
      </Typography>

    </div>
  );
};

export default AdminOverviewPage;
